import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Lock, Sparkle, X, Check } from "@phosphor-icons/react";

// Global bus: any place in the app can dispatch a 402/PLAN_QUOTA_EXCEEDED
// payload via `window.dispatchEvent(new CustomEvent("orixa:quota-exceeded", { detail }))`.
// The modal listens and renders. Kept as a plain event bus to avoid a new
// context provider only for this modal.
export const QUOTA_EVENT = "orixa:quota-exceeded";

const REASON_COPY = {
  not_included: {
    heading: "Feature not included in your plan",
    body: "This action requires a higher subscription tier. Upgrade to unlock it and continue.",
  },
  limit_reached: {
    heading: "Monthly quota reached",
    body: "You have used your allowance for this billing period. Upgrade to increase your limits — no interruption to your work.",
  },
};

const TARGET_BENEFITS = {
  starter: [
    "10 strategic assessments / month",
    "3 AI Investment Documents / month",
    "PDF · DOCX · PPTX exports",
    "Email support",
  ],
  professional: [
    "Unlimited assessments",
    "Unlimited AI Investment Documents",
    "Advanced intelligence agents",
    "Regulatory & risk depth",
    "Priority support",
  ],
  enterprise: [
    "Everything in Professional",
    "Priority support",
    "Custom workflows",
    "Private deployment · API access",
  ],
};

export default function UpgradeModal() {
  const [payload, setPayload] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onEvent = (e) => {
      setPayload(e.detail || null);
    };
    window.addEventListener(QUOTA_EVENT, onEvent);
    return () => window.removeEventListener(QUOTA_EVENT, onEvent);
  }, []);

  if (!payload) return null;

  const reason = REASON_COPY[payload.reason] || REASON_COPY.limit_reached;
  const target = payload.upgrade_to || "professional";
  const targetLabel = payload.upgrade_label || (target.charAt(0).toUpperCase() + target.slice(1));
  const benefits = TARGET_BENEFITS[target] || TARGET_BENEFITS.professional;
  const isEnterprise = target === "enterprise";

  const close = () => setPayload(null);
  const upgrade = () => {
    close();
    if (isEnterprise) {
      window.open("https://cal.com/romuald-ouattara-wttutm/call-orixa-invest", "_blank", "noopener,noreferrer");
    } else {
      navigate(`/pricing?plan=${target}`);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      data-testid="upgrade-modal"
      onClick={close}
    >
      <div
        className="relative w-full max-w-lg bg-card border border-border rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="absolute top-4 right-4 h-8 w-8 rounded-md hover:bg-secondary grid place-items-center text-muted-foreground"
          onClick={close}
          data-testid="upgrade-modal-close"
          aria-label="Close"
        >
          <X size={16} weight="bold" />
        </button>

        {/* Header band */}
        <div className="bg-primary text-primary-foreground p-6 sm:p-8">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold opacity-80 mb-3">
            <Lock size={12} weight="fill" /> {payload.current_plan_label || payload.current_plan} plan
          </div>
          <div className="font-display text-2xl leading-tight tracking-tight">
            {reason.heading}
          </div>
          <div className="mt-2 text-sm opacity-90">
            {payload.metric_label ? (
              <>
                {payload.metric_label}
                {payload.limit != null && payload.limit !== false && payload.limit !== 0 && (
                  <> · used {payload.used}/{payload.limit} this period</>
                )}
              </>
            ) : (
              reason.body
            )}
          </div>
        </div>

        {/* Body */}
        <div className="p-6 sm:p-8">
          <div className="text-sm text-muted-foreground leading-relaxed">{reason.body}</div>

          <div className="mt-6 rounded-xl border border-border bg-secondary/40 p-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-overline text-primary">Recommended upgrade</div>
                <div className="mt-0.5 font-display text-xl tracking-tight">Orixa {targetLabel}</div>
              </div>
              <Sparkle size={20} weight="duotone" className="text-gold" />
            </div>
            <ul className="mt-4 space-y-2">
              {benefits.map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <Check size={14} weight="bold" className="text-teal mt-0.5 flex-shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <button
              onClick={close}
              className="flex-1 border border-border rounded-lg px-5 py-2.5 text-sm font-semibold hover:bg-secondary"
              data-testid="upgrade-modal-later"
            >
              Not now
            </button>
            <button
              onClick={upgrade}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-lg px-5 py-2.5 text-sm font-semibold hover:opacity-90 card-elev"
              data-testid="upgrade-modal-cta"
            >
              {isEnterprise ? "Talk to enterprise sales" : `Upgrade to ${targetLabel}`}
              <ArrowRight size={14} weight="bold" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
